import React, { useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { productService } from '../services/api';
import ProductCard from '../components/ProductCard';
import VatucoLogo from '../components/VatucoLogo';
import MobileShell from './components/MobileShell';

/**
 * Pantalla principal mobile.
 *
 * - Sin sesión: landing app-style con CTAs grandes (registro / login) y
 *   features swipeables.
 * - Con sesión: arriba un acceso rápido y llamativo al álbum + tres atajos
 *   chicos a Comunidades / Compraventas / Mensajes. Debajo, el feed de
 *   productos en 2 columnas con infinite scroll — replicando lo que hacía
 *   la home desktop, pero adaptado a celu.
 */
const HERO_FEATURES = [
  { icon: 'photo_library', title: 'Álbum global',  body: '994 figuritas reales. SmartTrade encuentra con quién intercambiar.' },
  { icon: 'storefront',    title: 'Marketplace',   body: 'Comprá y vendé adentro de tus comunidades. Confianza real.' },
  { icon: 'groups',        title: 'Comunidades',   body: 'Tribus por barrio, club o hobby. Sin algoritmos opacos.' },
];

const QUICK_TILES = [
  { path: '/m/communities',  icon: 'groups',       label: 'Comunidades' },
  { path: '/m/reservations', icon: 'shopping_bag', label: 'Compraventas' },
  { path: '/m/messages',     icon: 'forum',        label: 'Mensajes' },
];

export default function MobileHome() {
  const navigate = useNavigate();
  const { user } = useAuth();

  if (!user) return <LoggedOutHome navigate={navigate} />;
  return <LoggedInHome navigate={navigate} user={user} />;
}

/* ── Logged-in: feed dominante + álbum como hero ───────────────────── */

function LoggedInHome({ navigate, user }) {
  const {
    data: pages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: ['mobile-home-products'],
    queryFn: async ({ pageParam }) => {
      const r = await productService.getProductCards({ pageSize: 16, cursor: pageParam });
      return r.data;
    },
    initialPageParam: undefined,
    getNextPageParam: (last) => last?.hasMore ? last.nextCursor : undefined,
  });

  const products = useMemo(
    () => pages?.pages?.flatMap(p => Array.isArray(p) ? p : (p.data ?? p.items ?? [])) ?? [],
    [pages],
  );

  // Infinite scroll vía IntersectionObserver
  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!sentinelRef.current || !hasNextPage) return;
    const obs = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && !isFetchingNextPage) fetchNextPage();
    }, { rootMargin: '300px 0px' });
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <MobileShell>
      {/* ── Saludo ── */}
      <section className="px-5 pt-5 pb-2">
        <p className="text-xs text-on-surface-variant">Hola</p>
        <h1 className="mt-1 text-2xl font-black tracking-tight">
          {user.name?.split(' ')[0] || 'Coleccionista'}
        </h1>
      </section>

      {/* ── Álbum hero ── */}
      <section className="px-5 pb-3">
        <button
          type="button"
          onClick={() => navigate('/m/album')}
          className="w-full bg-primary text-on-primary rounded-xl p-5 text-left shadow-lg shadow-primary/30 active:scale-[0.99] transition-transform"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center">
              <span className="material-symbols-outlined text-2xl">photo_library</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-base font-black">Mi álbum</p>
              <p className="text-xs opacity-90">Coleccioná, intercambiá, encontrá matches</p>
            </div>
            <span className="material-symbols-outlined">arrow_forward</span>
          </div>
        </button>
      </section>

      {/* ── Atajos chicos ── */}
      <section className="px-5 pb-4">
        <div className="grid grid-cols-3 gap-2">
          {QUICK_TILES.map(t => (
            <button
              key={t.path}
              type="button"
              onClick={() => navigate(t.path)}
              className="flex flex-col items-center justify-center gap-1.5 py-3 rounded-xl border border-outline-variant bg-surface active:bg-surface-container transition-colors"
            >
              <span className="material-symbols-outlined text-xl text-primary">{t.icon}</span>
              <span className="text-[11px] font-bold text-on-surface">{t.label}</span>
            </button>
          ))}
        </div>
      </section>

      {/* ── Feed de productos ── */}
      <section className="px-5 pb-6">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-lg font-black tracking-tight text-on-surface">Productos</h2>
          <button
            type="button"
            onClick={() => navigate('/search')}
            className="text-xs font-bold text-primary"
          >
            Buscar
          </button>
        </div>

        {isLoading && (
          <div className="grid grid-cols-2 gap-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="aspect-[3/4] bg-surface-container animate-pulse rounded-lg" />
            ))}
          </div>
        )}

        {!isLoading && products.length === 0 && (
          <div className="py-12 text-center text-sm text-on-surface-variant">
            <span className="material-symbols-outlined text-4xl text-outline-variant">inventory_2</span>
            <p className="mt-2">Todavía no hay productos en tus comunidades.</p>
            <button
              type="button"
              onClick={() => navigate('/m/communities')}
              className="mt-3 px-4 py-2 rounded-lg bg-primary text-on-primary font-bold text-xs"
            >
              Explorar comunidades
            </button>
          </div>
        )}

        {!isLoading && products.length > 0 && (
          <>
            <div className="grid grid-cols-2 gap-3">
              {products.map(p => (
                <ProductCard
                  key={p.id}
                  product={p}
                  onClick={() => navigate(`/products/${p.id}`)}
                />
              ))}
            </div>
            <div ref={sentinelRef} className="h-8 mt-4 flex items-center justify-center">
              {isFetchingNextPage && (
                <span className="text-xs text-on-surface-variant">Cargando más…</span>
              )}
            </div>
          </>
        )}
      </section>
    </MobileShell>
  );
}

/* ── Logged-out: landing ───────────────────────────────────────────── */

function LoggedOutHome({ navigate }) {
  return (
    <MobileShell hideTabBar>
      <section className="px-5 pt-12 pb-8">
        <VatucoLogo className="text-xs uppercase tracking-widest" />
        <h1 className="mt-2 text-4xl font-black tracking-tight text-on-surface leading-tight">
          La comunidad<br />que colecciona.
        </h1>
        <p className="mt-4 text-base text-on-surface-variant leading-relaxed">
          Coleccioná en serio. Intercambiá con personas reales. Y mientras tanto, descubrí lo que tu comunidad vende.
        </p>
        <div className="mt-8 flex flex-col gap-3">
          <button
            type="button"
            onClick={() => navigate('/register')}
            className="w-full min-h-[52px] rounded-xl bg-primary text-on-primary font-bold text-base shadow-lg shadow-primary/30"
          >
            Empezar gratis
          </button>
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="w-full min-h-[52px] rounded-xl border border-outline-variant text-on-surface font-bold text-base"
          >
            Ya tengo cuenta
          </button>
        </div>
      </section>

      <section className="px-5 pb-12">
        <p className="text-[10px] font-black uppercase tracking-widest text-on-surface-variant mb-3">
          Qué hacés acá
        </p>
        <div className="-mx-5 px-5 flex gap-3 overflow-x-auto snap-x snap-mandatory hide-scrollbar pb-1">
          {HERO_FEATURES.map(f => (
            <div key={f.title} className="snap-center shrink-0 w-[78%] max-w-[320px] bg-surface-container rounded-xl p-5 border border-outline-variant">
              <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center">
                <span className="material-symbols-outlined text-primary text-2xl">{f.icon}</span>
              </div>
              <h3 className="mt-3 text-base font-black text-on-surface">{f.title}</h3>
              <p className="mt-1 text-sm text-on-surface-variant leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-5 pb-16">
        <div className="rounded-xl border border-primary/40 bg-primary/5 p-5">
          <p className="text-sm font-bold text-on-surface">Ya estás listo.</p>
          <p className="mt-1 text-xs text-on-surface-variant">
            Crear tu cuenta toma 30 segundos. No te pedimos tarjeta.
          </p>
          <button
            type="button"
            onClick={() => navigate('/register')}
            className="mt-3 w-full min-h-[44px] rounded-xl bg-primary text-on-primary font-bold text-sm"
          >
            Crear mi cuenta
          </button>
        </div>
      </section>
    </MobileShell>
  );
}
